import { useCallback, useEffect, useState, useRef } from "react";
import { models } from "jiffy-api";
import * as ActionCable from "@rails/actioncable";
import { objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";

type WindowWithApp = Window & { App?: { cable?: ActionCable.Consumer } };

type ActionCableConsumer = ActionCable.Consumer;
type ActionCableSubscription = ActionCable.Subscription;
type DtfChannelActions = models.cable.DtfChannel.Actions;
type PerformCommands = models.cable.Perform.Commands;

const LOG_PREFIX = "ActionCable";

interface UseActionCableOptions {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onMessageReceived?: (data: DtfChannelActions) => void;
  onError?: (error: Error) => void;
  /** If provided, debounces duplicate perform() calls within this window (ms) */
  debounceMs?: number;
  /** Experiments object to inject into perform() data. If omitted, reads window.jiffy.experiments */
  experiments?: Record<string, unknown>;
  /**
   * Optional filter called before setting lastMessage.
   * Return false to skip updating lastMessage for this message.
   * The onMessageReceived callback is still called regardless.
   */
  messageFilter?: (data: DtfChannelActions) => boolean;
}

const initializeActionCableConsumer = (): ActionCableConsumer => {
  const w = window as WindowWithApp;

  if (!w.App) {
    w.App = {};
  }

  if (!w.App.cable) {
    w.App.cable = ActionCable.createConsumer();
  }

  return w.App.cable;
};

const log = (message: string, data?: unknown): void => {
  console.log(`${LOG_PREFIX}: ${message}`, data || "");
};

export function useActionCable(
  uuid: string | undefined,
  channel: string,
  options?: UseActionCableOptions
) {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [subscription, setSubscription] = useState<ActionCableSubscription | undefined>(undefined);
  const [lastMessage, setLastMessage] = useState<DtfChannelActions | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const subscriptionRef = useRef<ActionCableSubscription | undefined>(undefined);
  const optionsRef = useRef<UseActionCableOptions | undefined>(options);
  const lastPerformRef = useRef<{ command: string; timestamp: number } | null>(null);
  const activeSubscriptionKey = useRef<string | null>(null);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const perform = useCallback(
    (command: PerformCommands): void => {
      const debounceMs = optionsRef.current?.debounceMs;
      const commandKey = `${command.command}-${JSON.stringify(command.data || {})}`;

      if (debounceMs !== undefined) {
        const now = Date.now();

        if (
          lastPerformRef.current &&
          lastPerformRef.current.command === commandKey &&
          now - lastPerformRef.current.timestamp < debounceMs
        ) {
          log(`Debouncing duplicate command: ${command.command}`);
          return;
        }
      }

      if (!subscription) {
        const performError = new Error("Cannot perform action: not connected to ActionCable");
        setError(performError);
        optionsRef.current?.onError?.(performError);
        return;
      }

      try {
        const experiments = optionsRef.current?.experiments || window.jiffy?.experiments || {};
        const dataWithExperiments = { ...command.data, experiments };
        const transformedData = objNamesToSnakeCase(dataWithExperiments);
        subscription.perform(command.command, transformedData);
        log(`Performed command: ${command.command}`, transformedData);

        if (debounceMs !== undefined) {
          lastPerformRef.current = { command: commandKey, timestamp: Date.now() };
        }
      } catch (err) {
        const performError =
          err instanceof Error ? err : new Error("Unknown error during command execution");
        setError(performError);
        optionsRef.current?.onError?.(performError);
      }
    },
    [subscription]
  );

  const unsubscribe = useCallback((): void => {
    if (subscription) {
      try {
        subscription.unsubscribe();
        setSubscription(undefined);
        subscriptionRef.current = undefined;
        setIsConnected(false);
        setError(undefined);
        activeSubscriptionKey.current = null;
        log("Successfully unsubscribed from ActionCable");
      } catch (err) {
        const unsubscribeError = err instanceof Error ? err : new Error("Error during unsubscribe");
        setError(unsubscribeError);
        optionsRef.current?.onError?.(unsubscribeError);
      }
    }
  }, [subscription]);

  useEffect(() => {
    if (!uuid) {
      return undefined;
    }

    const subscriptionKey = `${channel}:${uuid}`;

    if (activeSubscriptionKey.current === subscriptionKey) {
      log(`Already subscribed to ${subscriptionKey}, skipping duplicate`);
      return undefined;
    }

    try {
      const consumer = initializeActionCableConsumer();
      log(`Connecting to channel: ${channel} with UUID: ${uuid}`);

      const cableSubscription = consumer.subscriptions.create(
        { uuid, channel },
        {
          connected(): void {
            setIsConnected(true);
            setError(undefined);
            log(`Connected to ${channel} with UUID: ${uuid}`);
            optionsRef.current?.onConnected?.();
          },
          disconnected(): void {
            setIsConnected(false);
            log(`Disconnected from ${channel}`);
            optionsRef.current?.onDisconnected?.();
          },
          received(data: unknown): void {
            try {
              const transformedData = objNamesToCamelCase(data) as DtfChannelActions;

              const shouldSet = optionsRef.current?.messageFilter
                ? optionsRef.current.messageFilter(transformedData)
                : true;

              if (shouldSet) {
                setLastMessage(transformedData);
              }

              log(`Message received from ${channel}`, data);
              optionsRef.current?.onMessageReceived?.(transformedData);
            } catch (err) {
              const receivedError =
                err instanceof Error ? err : new Error("Error processing received message");
              setError(receivedError);
              optionsRef.current?.onError?.(receivedError);
            }
          },
          rejected(): void {
            const rejectedError = new Error(`Connection rejected for channel: ${channel}`);
            setError(rejectedError);
            setIsConnected(false);
            optionsRef.current?.onError?.(rejectedError);
          }
        }
      );

      setSubscription(cableSubscription);
      subscriptionRef.current = cableSubscription;
      activeSubscriptionKey.current = subscriptionKey;
    } catch (err) {
      const connectError =
        err instanceof Error ? err : new Error("Error creating ActionCable subscription");
      setError(connectError);
      optionsRef.current?.onError?.(connectError);
    }

    return () => {
      if (subscriptionRef.current) {
        try {
          log("Cleaning up subscription");
          subscriptionRef.current.unsubscribe();
        } catch {
          log("Error during cleanup unsubscribe");
        }
      }
      setSubscription(undefined);
      setIsConnected(false);
      activeSubscriptionKey.current = null;
    };
  }, [uuid, channel]);

  return {
    isConnected,
    lastMessage,
    error,
    perform,
    unsubscribe
  };
}
